import React, { useState } from 'react';
import { auth, db } from '../firebase';
import { updatePassword, signOut, getIdToken } from 'firebase/auth';
import { doc, updateDoc } from 'firebase/firestore';
import { Lock, AlertCircle, CheckCircle2 } from 'lucide-react';

interface Props {
    onPasswordChanged: () => void;
}

export const ChangePasswordScreen: React.FC<Props> = ({ onPasswordChanged }) => {
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        if (newPassword.length < 6) {
            setError("Password must be at least 6 characters long.");
            return;
        }

        if (newPassword !== confirmPassword) {
            setError("Passwords do not match.");
            return;
        }

        if (!auth.currentUser) {
            setError("You must be logged in to change your password.");
            return;
        }

        setLoading(true);
        try {
            // Update password in Auth
            await updatePassword(auth.currentUser, newPassword);

            // Remove the flag in Firestore so they aren't asked again
            const userRef = doc(db, "users", auth.currentUser.uid);
            await updateDoc(userRef, { requiresPasswordChange: false });

            // Force a fresh ID token so all subsequent Firestore/function calls
            // use valid credentials and don't hit permission errors.
            await getIdToken(auth.currentUser, true);

            setSuccess(true);
            
            // Allow them to read success message before navigating away
            setTimeout(() => {
                onPasswordChanged();
            }, 1500);

        } catch (err: any) {
            console.error("Error updating password:", err);
            if (err.code === 'auth/requires-recent-login') {
                // Token is too stale to change password — sign out so the user
                // is forced to re-authenticate cleanly before trying again.
                setError("For security, please log back in and then set your new password.");
                setTimeout(async () => {
                    await signOut(auth);
                }, 2000);
            } else {
                setError(`Failed to update password: ${err.message}`);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = async () => {
        await signOut(auth);
    };

    return (
        <div style={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
            <div className="card" style={{ padding: '2.5rem', width: '100%', maxWidth: '450px' }}>
                <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
                    <div style={{ width: '64px', height: '64px', background: 'var(--primary-soft)', borderRadius: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem', color: 'var(--primary)' }}>
                        <Lock size={32} />
                    </div>
                    <h1 style={{ fontSize: '1.75rem', fontWeight: 700, marginBottom: '0.5rem' }}>
                        Password Reset Required
                    </h1>
                    <p style={{ color: 'var(--text-secondary)', fontSize: '0.95rem' }}>
                        Your password has been reset by an administrator. Please secure your account by choosing a new password.
                    </p>
                </div>

                {success ? (
                    <div style={{ textAlign: 'center', padding: '2rem 0' }}>
                        <CheckCircle2 size={64} style={{ color: 'var(--success)', margin: '0 auto 1rem' }} />
                        <h2 style={{ fontSize: '1.25rem', marginBottom: '0.5rem' }}>Password Updated!</h2>
                        <p style={{ color: 'var(--text-secondary)' }}>Connecting you to your dashboard...</p>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                        
                        <div className="form-group-fancy">
                            <label>New Password</label>
                            <input
                                type="password"
                                className="input-fancy"
                                placeholder="..."
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                minLength={6}
                                required
                            />
                        </div>

                        <div className="form-group-fancy">
                            <label>Confirm Password</label>
                            <input
                                type="password"
                                className="input-fancy"
                                placeholder="..."
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                minLength={6}
                                required
                            />
                        </div>

                        {error && (
                            <div className="fb-point fb-imp" style={{ padding: '0.75rem 1rem' }}>
                                <AlertCircle size={16} />
                                <span style={{ fontSize: '0.85rem' }}>{error}</span>
                            </div>
                        )}

                        <button 
                            type="submit" 
                            className="button button-primary" 
                            style={{ width: '100%', padding: '0.85rem' }} 
                            disabled={loading || newPassword.length < 6 || confirmPassword.length < 6}
                        >
                            {loading ? 'Updating...' : 'Set New Password'}
                        </button>
                        
                        <div style={{ textAlign: 'center', marginTop: '1rem' }}>
                            <button 
                                type="button" 
                                onClick={handleLogout} 
                                style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '0.9rem', textDecoration: 'underline' }}
                            >
                                Cancel and Logout
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};
